import React from 'react';
import {
	Box,
	Card,
	Chip,
	Divider,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { ExpandMore, ExpandLess } from 'components/icons';
import WarningOutlinedIcon from '@mui/icons-material/WarningOutlined';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { Ticket } from '../../types/ticket';
import { ParkingLocation } from '../../types/parkingLocation';
import { stateProvinceList } from '../../data/stateProvinceList';
import { mapTicketTypeToColor } from '../../utils/mapTicketTypeToColor';
import { useTicketTypeMeta } from '../../context/TicketTerminologyProvider';
import TicketAttachments from '../ticketAttachments/ticketAttachments';

interface TicketRecordProps {
	showContentOnLoad?: boolean;
	ticket: Ticket;
	parkingLocation: ParkingLocation;
}

interface FieldProps {
	label: string;
	value: React.ReactNode;
	muted?: boolean;
}

const Field: React.FC<FieldProps> = ({ label, value, muted }) => (
	<Stack spacing={0.25} sx={{ minWidth: 0 }}>
		<Typography
			variant="overline"
			color="text.secondary"
			sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
		>
			{label}
		</Typography>
		<Typography
			variant="body2"
			fontWeight={300}
			color={muted ? 'text.secondary' : 'text.primary'}
			sx={{ wordBreak: 'break-word' }}
		>
			{value}
		</Typography>
	</Stack>
);

const SectionGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<Box
		sx={{
			display: 'grid',
			gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
			columnGap: 2,
			rowGap: 1.25,
		}}
	>
		{children}
	</Box>
);

const TicketRecord: React.FC<TicketRecordProps> = ({
	ticket,
	showContentOnLoad,
	parkingLocation,
}) => {
	const getTicketTypeMeta = useTicketTypeMeta();
	const [showContent, setShowContent] = React.useState<boolean>(
		showContentOnLoad ?? false,
	);
	const {
		ticketNumber,
		ticketType,
		licenseNumber,
		licenseState,
		parkingZone,
		date,
		vehicleInfo,
		issuedBy,
		comments,
		attachments,
		isVoid,
		isWithinWindow,
	} = ticket;

	// Out-of-window tickets are shown for context only (greyed) and don't count
	// toward the violation decision. Guard on `=== false` so tickets without the
	// flag (lot history, ticket modal) render normally.
	const isOutOfWindow = isWithinWindow === false && !isVoid;

	const stateObj = stateProvinceList.find((s) => s.code === licenseState);
	const licenseStateLabel = stateObj ? stateObj.label : licenseState;

	const isoDate = new Date(date);
	const fullDate = format(isoDate, 'EEEE, do MMMM yyyy, h:mm a');
	const relativeDate = `${formatDistanceToNowStrict(isoDate)} ago`;

	const { shortLabel, hexColor } = getTicketTypeMeta(ticketType);
	const chipColor = mapTicketTypeToColor(ticketType);

	const plateLine = `${licenseNumber} (${licenseStateLabel})`;
	const locationLine = `${parkingLocation?.name}${
		parkingZone ? ` — ${parkingZone}` : ''
	}`;
	const hasComments = !!comments && comments.trim().length > 0;
	const hasAttachments = attachments && attachments.length > 0;
	const hasVehicleInfo = !!vehicleInfo && vehicleInfo.trim().length > 0;

	return (
		<Card
			variant="outlined"
			sx={{
				width: '100%',
				borderRadius: 2,
				borderLeft: `4px solid ${hexColor}`,
				opacity: isVoid ? 0.85 : isOutOfWindow ? 0.6 : 1,
			}}
		>
			<Box
				role="button"
				aria-expanded={showContent}
				onClick={() => setShowContent((prev) => !prev)}
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					p: 1,
					pl: 1.25,
					cursor: 'pointer',
					'&:hover': { backgroundColor: 'action.hover' },
				}}
			>
				<Box sx={{ flexGrow: 1, minWidth: 0 }}>
					<Stack
						direction="row"
						alignItems="center"
						spacing={1}
						sx={{ flexWrap: 'wrap' }}
					>
						<Chip
							label={shortLabel}
							color={chipColor}
							size="small"
							sx={{ fontWeight: 500 }}
						/>
						<Typography
							variant="body2"
							fontWeight={400}
							sx={{
								textDecoration: isVoid ? 'line-through' : 'none',
							}}
						>
							{relativeDate}
						</Typography>
						{isVoid && (
							<Chip
								label="Void"
								size="small"
								color="default"
								variant="outlined"
							/>
						)}
					</Stack>
					{isOutOfWindow && (
						<Box sx={{ display: 'flex', mt: 0.5 }}>
							<Chip
								label="Outside lookup window"
								size="small"
								color="default"
								variant="outlined"
							/>
						</Box>
					)}
					<Typography
						variant="caption"
						color="text.secondary"
						fontWeight={300}
						sx={{
							display: 'block',
							mt: 0.25,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{plateLine} · {parkingZone || locationLine} ·{' '}
						{issuedBy || 'Unknown issuer'}
					</Typography>
				</Box>
				<IconButton
					size="small"
					aria-label={showContent ? 'Collapse' : 'Expand'}
					onClick={(e) => {
						e.stopPropagation();
						setShowContent((prev) => !prev);
					}}
				>
					{showContent ? <ExpandLess /> : <ExpandMore />}
				</IconButton>
			</Box>

			{showContent && (
				<Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
					{isVoid && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								p: 1,
								mb: 1.5,
								borderRadius: 1,
								backgroundColor: 'action.hover',
								color: 'text.secondary',
							}}
						>
							<WarningOutlinedIcon fontSize="small" />
							<Typography variant="body2" fontWeight={400}>
								This record has been voided
							</Typography>
						</Box>
					)}

					<Stack spacing={1.5} divider={<Divider flexItem />}>
						<SectionGrid>
							<Field label="Date" value={fullDate} />
							<Field label="Record #" value={ticketNumber} />
							<Field label="Location" value={locationLine} />
						</SectionGrid>

						<SectionGrid>
							<Field label="License Plate" value={plateLine} />
							<Field
								label="Vehicle"
								value={hasVehicleInfo ? vehicleInfo : 'Not provided'}
								muted={!hasVehicleInfo}
							/>
						</SectionGrid>

						<Stack spacing={1.25}>
							<Field
								label="Issued By"
								value={issuedBy || 'Unknown'}
								muted={!issuedBy}
							/>
							{hasComments && (
								<Field label="Comments" value={comments} />
							)}
						</Stack>

						{hasAttachments && (
							<Stack spacing={1}>
								<Typography
									variant="overline"
									color="text.secondary"
									sx={{ lineHeight: 1.2, letterSpacing: '0.08em' }}
								>
									Attachments
								</Typography>
								<TicketAttachments
									attachments={attachments}
									disableDelete={true}
								/>
							</Stack>
						)}
					</Stack>
				</Box>
			)}
		</Card>
	);
};

export default TicketRecord;
